import {skipToken} from '@reduxjs/toolkit/query';
import {
  DropdownActionMenuItem,
  openConfirmDialog,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useCloseWorkshopInterestMutation,
  useDeleteWorkshopInterestMutation,
  useGetSvCloudBookingUrlQuery,
  useGetWorkshopInterestQuery,
  useOpenWorkshopInterestMutation,
  useReopenWorkshopInterestMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {EntityHeaderAction, handleApiError, useWorkshopInterestEditPermissions} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {WorkshopInterestEndCaseDialog} from '../components/WorkshopInterestEndCaseDialog';

const SV_CLOUD_RESOURCE_CODE = 'service-interest';

// eslint-disable-next-line complexity -- Pre-existing violation
export function useWorkshopInterestActions(interestId: string) {
  const navigate = useNavigate();

  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: interestId});

  const [deleteWorkshopInterest] = useDeleteWorkshopInterestMutation();
  const [openWorkshopInterest, {isLoading: isOpenLoading}] = useOpenWorkshopInterestMutation();
  const [closeWorkshopInterest, {isLoading: isCloseLoading}] = useCloseWorkshopInterestMutation();
  const [reopenWorkshopInterest, {isLoading: isReopenLoading}] =
    useReopenWorkshopInterestMutation();

  const {
    hasOpenPermission,
    hasEndPermission,
    hasClosePermission,
    hasReopenPermission,
    hasDeletePermission,
    hasScheduleVisitPermission,
  } = useWorkshopInterestEditPermissions({interestId});

  const canDelete =
    (workshopInterest?.availableActions?.includes('DELETE') ?? false) && hasDeletePermission;
  const isOpenAvailable =
    workshopInterest?.state === 'NEW' &&
    (workshopInterest?.availableActions?.includes('OPEN') ?? false);
  // Both closings are terminal, so the backend drops CLOSE and SCHEDULE_VISIT once one exists.
  // Deriving both from availableActions keeps it the single authority on what is still possible.
  const canClose = workshopInterest?.availableActions?.includes('CLOSE') ?? false;
  const canReopen = workshopInterest?.availableActions?.includes('REOPEN') ?? false;
  const canScheduleVisit = workshopInterest?.availableActions?.includes('SCHEDULE_VISIT') ?? false;

  const plannerUrl = workshopInterest?.scheduledVisit?.plannerUrl;
  const isVisitPlanned = isNotNilOrEmpty(plannerUrl);

  const branchId = workshopInterest?.branchId;
  // Only resolve the booking-wizard URL while the visit is not planned yet;
  // once planned, the button links to the existing visit via plannerUrl instead.
  const {data: svCloudBooking} = useGetSvCloudBookingUrlQuery(
    isNotNil(branchId) && !isVisitPlanned && canScheduleVisit
      ? {branchId, resourceCode: SV_CLOUD_RESOURCE_CODE, resourceId: interestId}
      : skipToken
  );

  const bookingUrl = svCloudBooking?.bookingUrl;
  const hasBookingUrl = isNotNilOrEmpty(bookingUrl);

  const handleScheduleVisitClick = () => {
    if (isNotNil(bookingUrl)) {
      window.open(bookingUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenPlannedVisitClick = () => {
    if (isNotNil(plannerUrl)) {
      window.open(plannerUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const onEndClick = () =>
    openDialog(
      <WorkshopInterestEndCaseDialog
        data-testid={testIds.workshop.interestDetail('endCaseDialog')}
        interestId={interestId}
      />,
      {
        'data-testid': testIds.workshop.interestDetail('endCaseDialog'),
        title: i18n.t('entity.workshopInterest.labels.endInterestCase'),
        scrollBehavior: 'outside',
      }
    );

  // A successful closing is irreversible - there is no reopen action - and the endpoint takes no
  // input, so a confirmation is the only place the outcome gets spelled out to the user.
  const onCloseClick = () =>
    openConfirmDialog({
      text: i18n.t('entity.workshopInterest.labels.closeInterestSuccessfullyConfirm'),
      onConfirm: () =>
        closeWorkshopInterest({id: interestId})
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('general.notifications.successfullyCompleted'))
          )
          .catch(handleApiError),
      'data-testid': testIds.workshop.interestDetail('closeInterestDialog'),
    });

  const onReopenClick = () =>
    openConfirmDialog({
      text: i18n.t('entity.workshopInterest.labels.confirmReopen'),
      onConfirm: () => reopenWorkshopInterest({id: interestId}).unwrap().catch(handleApiError),
      'data-testid': testIds.workshop.interestDetail('reopenInterestDialog'),
    });

  const dropdownMenuItems = buildArray<DropdownActionMenuItem>().when(canDelete, {
    label: i18n.t('general.actions.delete'),
    onClick: () =>
      openDeleteDialog({
        onConfirm: () =>
          deleteWorkshopInterest({id: interestId})
            .unwrap()
            .then(() => {
              showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
              navigate(interestRoutes.workshopOverview);
            })
            .catch(handleApiError),
      }),
  });

  return buildArray<EntityHeaderAction>()
    .when(isVisitPlanned, {
      type: 'button',
      variant: 'secondary',
      leftIcon: 'action/calendar_today',
      title: i18n.t('entity.serviceScheduler.actions.planner'),
      onClick: handleOpenPlannedVisitClick,
      'data-testid': testIds.workshop.interestDetail('headerActions-plannedVisitDetail'),
    })
    .when(!isVisitPlanned && canScheduleVisit && hasBookingUrl, {
      type: 'button',
      variant: 'secondary',
      leftIcon: 'action/calendar_today',
      title: i18n.t('general.actions.schedule'),
      isDisabled: !hasScheduleVisitPermission,
      tooltip: hasScheduleVisitPermission ? undefined : i18n.t('general.labels.noPermission'),
      onClick: handleScheduleVisitClick,
      'data-testid': testIds.workshop.interestDetail('headerActions-scheduleVisit'),
    })
    .when(workshopInterest?.state === 'NEW' && dropdownMenuItems.length > 0, {
      type: 'dropdown-iconButton',
      priority: 'secondary',
      menuItems: dropdownMenuItems,
      'data-testid': testIds.workshop.interestDetail('headerActions-submenu'),
    })
    .when(workshopInterest?.state === 'OPEN', {
      type: 'button',
      variant: 'secondary',
      title: i18n.t('general.actions.end'),
      isDisabled: !hasEndPermission,
      tooltip: hasEndPermission ? undefined : i18n.t('general.labels.noPermission'),
      onClick: onEndClick,
      'data-testid': testIds.workshop.interestDetail('headerActions-end'),
    })
    .when(canClose, {
      type: 'button',
      variant: 'primary',
      title: i18n.t('general.actions.close'),
      isLoading: isCloseLoading,
      isDisabled: !hasClosePermission || isCloseLoading,
      tooltip: hasClosePermission ? undefined : i18n.t('general.labels.noPermission'),
      onClick: onCloseClick,
      'data-testid': testIds.workshop.interestDetail('headerActions-close'),
    })
    .when(isOpenAvailable, {
      type: 'button',
      variant: 'primary',
      title: i18n.t('general.actions.open'),
      isLoading: isOpenLoading,
      isDisabled: !hasOpenPermission || isOpenLoading,
      tooltip: hasOpenPermission ? undefined : i18n.t('general.labels.noPermission'),
      onClick: () => openWorkshopInterest({id: interestId}).unwrap().catch(handleApiError),
      'data-testid': testIds.workshop.interestDetail('headerActions-open'),
    })
    .when(canReopen, {
      type: 'button',
      variant: 'secondary',
      onClick: onReopenClick,
      isLoading: isReopenLoading,
      title: i18n.t('general.actions.reopen'),
      isDisabled: !hasReopenPermission,
      tooltip: !hasReopenPermission ? i18n.t('general.labels.noPermission') : undefined,
      'data-testid': testIds.workshop.interestDetail('headerActions-reopen'),
    });
}
